// src/components/Header.js
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLocationContext } from "../context/LocationContext";
import UserAvatar from "./UserAvatar";
import { useCart } from "../context/CartContext";
import { useCartDrawer } from "../context/CartDrawerContext";

const Header = ({ onLoginClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getTotalItems } = useCart();
  const { openCart } = useCartDrawer();
  const { location: currentLocation, status, error, requestLocation, clearError } = useLocationContext();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const isActive = (path) => location.pathname === path;

  // Scroll detection for sticky header effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (!isMobileMenuOpen) document.body.classList.add("mobile-menu-visible");
    else document.body.classList.remove("mobile-menu-visible");
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.classList.remove("mobile-menu-visible");
  };

  const handleCartClick = () => {
    navigate("/product");
  };

  const handleLocationClick = () => {
    requestLocation({ force: true });
  };

  const getDisplayLocation = () => {
    if (status === "loading") return "Detecting location...";
    if (error) return "Enable location";
    if (currentLocation) {
      console.log("----------",currentLocation);
      return currentLocation.address || currentLocation.city || `${currentLocation.latitude.toFixed(2)}, ${currentLocation.longitude.toFixed(2)}`;
    }
    return "Get Location";
  };

  // Function to open location in OpenStreetMap
  

  return (
    <>

      {/* Header Top Bar */}
      <div className="header-top-pro">
        <div className="top-bar-container">
          <div style={{ display: "flex", gap: "25px", flexWrap: "wrap" }}>
            <div
              className="top-info-item location-btn"
              onClick={handleLocationClick}
              title={currentLocation ? "Click to view location details" : "Click to get your location"}
              style={{ cursor: "pointer" }}
            >
              <i className="fas fa-map-marker-alt"></i>
              <span style={{ fontSize: "13px" }}>
                {getDisplayLocation()}
                {status === "loading" && (
                  <i className="fas fa-spinner fa-spin ms-2"></i>
                )}
                {currentLocation && status !== "loading" && (
                  <i className="fas fa-info-circle ms-2" style={{ fontSize: "12px" }}></i>
                )}
              </span>
            </div>

            <div className="top-info-item">
              <i className="far fa-clock"></i>
              <span>Training: 4:30am - 9:00pm</span>
            </div>

          </div>

          <div className="social-links">
            <span style={{ color: "#fff", fontSize: "13px", marginRight: "5px" }}>
              Follow Us:
            </span>
            <a href="/" className="social-link" aria-label="Facebook">
              <i className="fab fa-facebook-f"></i>
            </a>
            <a href="/" className="social-link" aria-label="Twitter">
              <i className="fab fa-twitter"></i>
            </a>
            <a href="/" className="social-link" aria-label="Instagram">
              <i className="fab fa-instagram"></i>
            </a>
            <a href="/" className="social-link" aria-label="LinkedIn">
              <i className="fab fa-linkedin-in"></i>
            </a>
            <a href="/" className="social-link" aria-label="YouTube">
              <i className="fab fa-youtube"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`header-main-pro ${isScrolled ? "scrolled" : ""}`}>
        <div className="main-header-container">
          <button
            className={`mobile-toggle-pro mobile-toggle-left ${isMobileMenuOpen ? "active" : ""}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span className="mobile-toggle-bar"></span>
            <span className="mobile-toggle-bar"></span>
            <span className="mobile-toggle-bar"></span>
          </button>

          {/* Logo Section */}
          <Link to="/" className="logo-section-pro">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/logonew1.png`}
              alt="ASTA Logo"
              className="logo-image-pro"
            />
            <div className="logo-text-pro">
              <h1 className="logo-title-pro">
                <span className="logo-title-full">Advance & Systematic Training Academy</span>
                <span className="logo-title-short">ASYSTA</span>
              </h1>
             
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            <ul className="nav-menu-pro">
              <li className={`nav-item-pro ${isActive("/") ? "active" : ""}`}>
                <Link to="/" className="nav-link-pro">
                  Why Us
                </Link>
              </li>
              <li className={`nav-item-pro ${isActive("/about") ? "active" : ""}`}>
                <Link to="/about" className="nav-link-pro">
                  About Us
                </Link>
              </li>
              <li className={`nav-item-pro ${isActive("/courses") ? "active" : ""}`}>
                <Link to="/courses" className="nav-link-pro">
                  Courses
                </Link>
              </li>
              <li className={`nav-item-pro ${isActive("/services") ? "active" : ""}`}>
                <Link to="/services" className="nav-link-pro">
                  Services
                </Link>
              </li>
              <li className={`nav-item-pro ${isActive("/product") ? "active" : ""}`}>
                <Link to="/product" className="nav-link-pro">
                  Products
                </Link>
              </li>
              <li className={`nav-item-pro ${isActive("/contact") ? "active" : ""}`}>
                <Link to="/contact" className="nav-link-pro">
                  Contact
                </Link>
              </li>
              <li className={`nav-item-pro ${isActive("/mocktest") ? "active" : ""}`}>
                <Link to="/mocktest" className="nav-link-pro">
                  Mock Test
                </Link>
              </li>
            </ul>
          </nav>

          {/* Header Actions */}
          <div className="header-actions-pro">
            {/* Cart Button */}
            <button
              className="cart-button-pro"
              onClick={openCart}
              aria-label="Shopping cart"
            >
              <i className="fas fa-shopping-cart cart-icon-pro"></i>
              {getTotalItems() > 0 && (
                <span className="cart-badge-pro">{getTotalItems()}</span>
              )}
            </button>

            {/* User Avatar or Login */}
            {user ? (
              <UserAvatar user={user} />
            ) : (
              <button className="login-button-pro" onClick={onLoginClick}>
                LOGIN
                <i className="fas fa-arrow-right"></i>
              </button>
            )}

          </div>
        </div>
      </header>

      {/* Sticky Header */}
      <div className={`sticky-header-pro ${isScrolled ? "show" : ""}`}>
        <div className="main-header-container">
          <button
            className={`mobile-toggle-pro mobile-toggle-left ${isMobileMenuOpen ? "active" : ""}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span className="mobile-toggle-bar"></span>
            <span className="mobile-toggle-bar"></span>
            <span className="mobile-toggle-bar"></span>
          </button>

          <Link to="/" className="logo-section-pro">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/logonew1.png`}
              alt="ASTA Logo"
              className="logo-image-pro"
            />
            <div className="logo-text-pro">
              <h1 className="logo-title-pro">
                <span className="logo-title-full">Advance & Systematic Training Academy</span>
                <span className="logo-title-short">ASYSTA</span>
              </h1>
             
            </div>
          </Link>

          <nav className="desktop-nav">
            <ul className="nav-menu-pro">
              <li className={`nav-item-pro ${isActive("/") ? "active" : ""}`}>
                <Link to="/" className="nav-link-pro">Why Us</Link>
              </li>
              <li className={`nav-item-pro ${isActive("/about") ? "active" : ""}`}>
                <Link to="/about" className="nav-link-pro">About Us</Link>
              </li>
              <li className={`nav-item-pro ${isActive("/courses") ? "active" : ""}`}>
                <Link to="/courses" className="nav-link-pro">Courses</Link>
              </li>
              <li className={`nav-item-pro ${isActive("/services") ? "active" : ""}`}>
                <Link to="/services" className="nav-link-pro">Services</Link>
              </li>
              <li className={`nav-item-pro ${isActive("/product") ? "active" : ""}`}>
                <Link to="/product" className="nav-link-pro">Products</Link>
              </li>
              <li className={`nav-item-pro ${isActive("/contact") ? "active" : ""}`}>
                <Link to="/contact" className="nav-link-pro">Contact</Link>
              </li>
              <li className={`nav-item-pro ${isActive("/mocktest") ? "active" : ""}`}>
                <Link to="/mocktest" className="nav-link-pro">Mock Test</Link>
              </li>
            </ul>
          </nav>

          <div className="header-actions-pro">
            <button className="cart-button-pro" onClick={openCart} aria-label="Shopping cart">
              <i className="fas fa-shopping-cart cart-icon-pro"></i>
              {getTotalItems() > 0 && (
                <span className="cart-badge-pro">{getTotalItems()}</span>
              )}
            </button>

            {user ? (
              <UserAvatar user={user} />
            ) : (
              <button className="login-button-pro" onClick={onLoginClick}>
                LOGIN
                <i className="fas fa-arrow-right"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Backdrop */}
      <div
        className={`menu-backdrop ${isMobileMenuOpen ? "show" : ""}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Mobile Menu */}
      <div className={`mobile-menu-pro ${isMobileMenuOpen ? "open" : ""}`}>
        <div className="mobile-menu-header">
          <div className="mobile-menu-brand">
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/logonew1.png`}
              alt="Asysta"
              className="mobile-menu-brand-logo"
            />
            <span className="mobile-menu-brand-text">ASYSTA</span>
          </div>
          <button
            className="mobile-close-btn"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <ul className="mobile-nav-list">
          <li className={`mobile-nav-item ${isActive("/") ? "active" : ""}`}>
            <Link to="/" className="mobile-nav-link" onClick={closeMobileMenu}>
              Why Us
            </Link>
          </li>
          <li className={`mobile-nav-item ${isActive("/about") ? "active" : ""}`}>
            <Link to="/about" className="mobile-nav-link" onClick={closeMobileMenu}>
              About Us
            </Link>
          </li>
          <li className={`mobile-nav-item ${isActive("/courses") ? "active" : ""}`}>
            <Link to="/courses" className="mobile-nav-link" onClick={closeMobileMenu}>
              Courses
            </Link>
          </li>
          <li className={`mobile-nav-item ${isActive("/services") ? "active" : ""}`}>
            <Link to="/services" className="mobile-nav-link" onClick={closeMobileMenu}>
              Services
            </Link>
          </li>
          <li className={`mobile-nav-item ${isActive("/product") ? "active" : ""}`}>
            <Link to="/product" className="mobile-nav-link" onClick={closeMobileMenu}>
              Products
            </Link>
          </li>
          <li className={`mobile-nav-item ${isActive("/contact") ? "active" : ""}`}>
            <Link to="/contact" className="mobile-nav-link" onClick={closeMobileMenu}>
              Contact
            </Link>
          </li>
          <li className={`mobile-nav-item ${isActive("/mocktest") ? "active" : ""}`}>
            <Link to="/mocktest" className="mobile-nav-link" onClick={closeMobileMenu}>
              Mock Test
            </Link>
          </li>
        </ul>

        {/* Location section in mobile menu */}
        {currentLocation && (
          <div className="mobile-location-section">
            <div className="mobile-location-header">
              <i className="fas fa-map-marker-alt"></i>
              <span>Your Location</span>
            </div>
            <div className="mobile-location-content">
              <p className="mobile-location-text">{currentLocation.address || currentLocation.city}</p>
              <button 
  className="mobile-location-btn"
  onClick={closeMobileMenu}
>

              
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      

      {/* Location Error Toast */}
      {error && (
        <div className="toast-notification toast-error">
          <div className="toast-icon">
            <i className="fas fa-exclamation-circle"></i>
          </div>
          <div className="toast-content">
            <p className="toast-title">Location Error</p>
            <p className="toast-message">{error}</p>
          </div>
          <button
            className="toast-close"
            onClick={clearError}
            aria-label="Close notification"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

    </>
  );
};

export default Header;
